import {
  LayoutDashboard,
  FileText,
  Package,
  MessageSquare,
  Settings,
  Database,
  Palette,
  ShoppingCart,
  Sparkles,
  Wrench,
  Search,
  Key,
  ShieldCheck,
  Server,
  Mail,
  BarChart2,
  GitBranch,
  Users,
  Table2,
} from 'lucide-react';

/** Stable ordering when click counts are tied or zero. */
export const PRIORITY_PATHS = [
  '/customer-identity',
  '/cia',
  '/purchase-program',
  '/order-x-v4',   // order-genie-group parent path — ranks the whole group
  '/ahri',             // ahri-group parent path — ranks the whole group
  '/open-search/ecommerce',
  '/agentic-shop',
  '/ai-visualizer',
  '/ai-tryout-studio',
  '/v4/documents',
  '/products',
  '/products/configurable',
  '/data-quality',
  '/realtime/connections',
  '/styles',
  '/configurations',
];

export const NAVIGATION_ITEMS = [
  {
    id: 'dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
    label: 'Dashboard',
    shortLabel: 'Dash',
    description: 'Overview and analytics',
  },
  {
    id: 'coupons',
    path: '/coupons',
    icon: Database,
    label: 'Coupons',
    shortLabel: 'Coupons',
    description: 'Coupon rules and execution',
  },
  {
    id: 'ai-visualizer',
    path: '/ai-visualizer',
    icon: Palette,
    label: 'AI Visualizer',
    shortLabel: 'Visualizer',
    description: 'Visual try-on and gallery experience',
  },
  {
    id: 'ahri-group',
    path: '/ahri',
    icon: Database,
    label: 'AHRI',
    shortLabel: 'AHRI',
    description: 'Dashboard, data sync, search, and system shop',
    children: [
      {
        id: 'ahri-dashboard',
        path: '/ahri/dashboard',
        icon: LayoutDashboard,
        label: 'AHRI Dashboard',
        shortLabel: 'Dashboard',
        description: 'AHRI sync, mapping, and pipeline health',
      },
      {
        id: 'ahri-data',
        path: '/ahri',
        icon: Database,
        label: 'AHRI Data',
        shortLabel: 'Data',
        description: 'Sync AHRI program data',
      },
      {
        id: 'ahri-assistant',
        path: '/ahri/assistant',
        icon: MessageSquare,
        label: 'AHRI Assistant',
        shortLabel: 'Assistant',
        description: 'Ask questions on AHRI data',
      },
      {
        id: 'ahri-search',
        path: '/ahri/solr',
        icon: Database,
        label: 'AHRI Search',
        shortLabel: 'Search',
        description: 'SolrCloud search & facets',
      },
      {
        id: 'ahri-systems-review',
        path: '/ahri/systems-review',
        icon: Search,
        label: 'AHRI Systems Review',
        shortLabel: 'Review',
        description: 'Server-side review with filters',
      },
      {
        id: 'ahri-customer-eligibility',
        path: '/ahri/customer-eligibility',
        icon: ShieldCheck,
        label: 'Customer Eligibility',
        shortLabel: 'Eligibility',
        description: 'Per-customer manufacturer/brand/category restrictions',
      },
      {
        id: 'ahri-eligibility-rule-sets',
        path: '/ahri/eligibility-rule-sets',
        icon: ShieldCheck,
        label: 'Eligibility Rule Sets',
        shortLabel: 'Rule Sets',
        description: 'Named, reusable eligibility rules built with a query builder',
      },
      {
        id: 'opensearch-config',
        path: '/ahri/opensearch-config',
        icon: Settings,
        label: 'OpenSearch Config',
        shortLabel: 'OS Config',
        description: 'Client-level embedding and vector settings',
      },
      {
        id: 'ahri-system-shop',
        path: '/ahri/shop',
        icon: ShoppingCart,
        label: 'AHRI System Shop',
        shortLabel: 'Shop',
        description: 'Browse & order AHRI certified systems',
      },
      {
        id: 'ahri-system-shop-v2',
        path: '/ahri/shop/v2',
        icon: ShoppingCart,
        label: 'AHRI System Shop v2',
        shortLabel: 'Shop v2',
        description: 'Embedded widget version of the AHRI system shop',
      },
      {
        id: 'ahri-system-shop-v3',
        path: '/ahri/shop/v3',
        icon: ShoppingCart,
        label: 'AHRI System Shop v3',
        shortLabel: 'Shop v3',
        description: 'Brand/eligibility-aware system shop widget (v6)',
      },
      {
        id: 'ahri-system-shop-compare',
        path: '/ahri/shop/compare',
        icon: ShoppingCart,
        label: 'System Shop Compare',
        shortLabel: 'Compare',
        description: 'Side-by-side AHRI system comparison',
      },
      {
        id: 'system-shop-events',
        path: '/ahri/system-shop-events',
        icon: Settings,
        label: 'System Shop Events',
        shortLabel: 'Events',
        description: 'Event list and webhook triggers',
      },
    ],
  },
  {
    id: 'semantix-search',
    path: '/semantix',
    icon: Sparkles,
    label: 'SemantiX Search',
    shortLabel: 'SemantiX',
    description: 'Search server settings + schema editor',
  },
  {
    id: 'open-search-ecommerce',
    path: '/open-search/ecommerce',
    icon: Search,
    label: 'OpenSearch Shop',
    shortLabel: 'OS Shop',
    description: 'Modern ecommerce hybrid product search',
  },
  {
    id: 'agentic-shop',
    path: '/agentic-shop',
    icon: Sparkles,
    label: 'Agentic Shop',
    shortLabel: 'Agentic',
    description: 'Isolated workspace for agentic commerce features',
  },
  {
    id: 'ai-visualize-admin',
    path: '/ai-tryout-studio',
    icon: Palette,
    label: 'AI Visualize Admin',
    shortLabel: 'Admin',
    description: 'Configure settings, swatches & previews',
  },
  {
    id: 'ai-reqpro',
    path: '/reqpro/receivers',
    icon: Settings,
    label: 'Ai ReqPro',
    shortLabel: 'ReqPro',
    description: 'EDI receive/post + transformers',
  },
  {
    id: 'documents',
    path: '/documents',
    icon: FileText,
    label: 'Documents',
    shortLabel: 'Docs',
    description: 'Manage and upload documents',
  },
  {
    id: 'documents-v4',
    path: '/v4/documents',
    icon: FileText,
    label: 'Documents v4',
    shortLabel: 'Docs v4',
    description: 'Rich markdown + Pinecone',
  },
  {
    id: 'products',
    path: '/products',
    icon: Package,
    label: 'Products',
    shortLabel: 'Products',
    description: 'Product management',
  },
  {
    id: 'configurable-products',
    path: '/products/configurable',
    icon: Wrench,
    label: 'Configurable Products',
    shortLabel: 'Config',
    description: 'Step-based product builder setup',
  },
  {
    id: 'contractors-hub',
    path: '/contractors-hub',
    icon: Users,
    label: 'Contractors Hub',
    shortLabel: 'Contractors',
    description: 'Publish ERP pricing to contractor ServiceTitan accounts',
  },
  {
    id: 'price-sheets',
    path: '/price-sheets',
    icon: Table2,
    label: 'Price Sheets',
    shortLabel: 'Price Sheets',
    description: 'Upload and manage master Excel price sheets for the ExtremeB2B widget',
  },
  {
    id: 'order-genie-group',
    path: '/order-x-v4',
    icon: ShoppingCart,
    label: 'OrderX',
    shortLabel: 'Orders',
    description: 'Process orders, email inbox and admin',
    children: [
      {
        id: 'order-genie-v4',
        path: '/order-x-v4',
        icon: ShoppingCart,
        label: 'OrderX V4 (New)',
        shortLabel: 'OX V4',
        description: 'Guided quote/order flow: match, price, customer, ship-to, shipping',
      },
      {
        id: 'order-genie-email-inbox',
        path: '/order-x/email-inbox',
        icon: Mail,
        label: 'OrderX Inbox',
        shortLabel: 'Inbox',
        description: 'Incoming emails processed by OrderX',
      },
      {
        id: 'order-genie-mailbox-setup',
        path: '/order-x/mailbox-setup',
        icon: Settings,
        label: 'Mailbox Setup',
        shortLabel: 'Mailbox',
        description: 'Configure IMAP mailbox sync and sender security',
      },
      {
        id: 'order-genie-account-admin',
        path: '/order-x/account-admin',
        icon: Settings,
        label: 'Queue & Admin',
        shortLabel: 'Admin',
        description: 'Request queue, limits, notifications and readiness',
      },
      {
        id: 'order-genie-mcp-server',
        path: '/order-x/mcp-server',
        icon: Server,
        label: 'MCP Server',
        shortLabel: 'MCP',
        description: 'OrderX tools via MCP (keys + playground)',
      },
      {
        id: 'order-genie-product-search',
        path: '/order-x/product-search',
        icon: Search,
        label: 'Product Search',
        shortLabel: 'Search',
        description: 'Search and test product matching',
      },
      {
        id: 'order-genie-usage-dashboard',
        path: '/order-x/usage-dashboard',
        icon: BarChart2,
        label: 'Usage Dashboard',
        shortLabel: 'Usage',
        description: 'Billable runs, extraction and match analytics',
      },
      {
        id: 'order-genie-workflow-setup',
        path: '/order-x/workflow-setup',
        icon: GitBranch,
        label: 'Workflow Setup',
        shortLabel: 'Workflows',
        description: 'Query types, flows and routing configuration',
      },
      {
        id: 'order-genie-settings',
        path: '/order-x/settings',
        icon: Settings,
        label: 'Settings',
        shortLabel: 'Settings',
        description: 'OrderX configuration and API settings',
      },
    ],
  },
  {
    id: 'purchase-program',
    path: '/purchase-program',
    icon: ShoppingCart,
    label: 'Purchase Program',
    shortLabel: 'Programs',
    description: 'Admin + client purchase controls',
  },
  {
    id: 'customer-identity-admin',
    path: '/customer-identity',
    icon: ShieldCheck,
    label: 'Customer Identity Admin',
    shortLabel: 'CI Admin',
    description: 'Sites, customers, platform users (uses your app login)',
  },
  {
    id: 'customer-identity-admin-portal',
    path: '/cia',
    icon: ShieldCheck,
    label: 'CI Admin Portal',
    shortLabel: 'CI Portal',
    description: 'Standalone admin portal with platform-user sign-in',
  },
  {
    id: 'styles',
    path: '/styles',
    icon: Palette,
    label: 'Styles',
    shortLabel: 'Styles',
    description: 'Theme settings for OrderX and AHRI',
  },
  {
    id: 'webhook-consumer',
    path: '/webhook-consumer',
    icon: Settings,
    label: 'Webhook Consumer',
    shortLabel: 'Webhook',
    description: 'Receive webhooks and map workflows',
  },
  {
    id: 'realtime-connections',
    path: '/realtime/connections',
    icon: MessageSquare,
    label: 'Realtime Connections',
    shortLabel: 'Realtime',
    description: 'Track websocket clients and push events',
  },
  {
    id: 'data-quality',
    path: '/data-quality',
    icon: Sparkles,
    label: 'Data Quality',
    shortLabel: 'Quality',
    description: 'AI-driven data analysis',
  },
  {
    id: 'chat',
    path: '/chat',
    icon: MessageSquare,
    label: 'Chat',
    shortLabel: 'Chat',
    description: 'AI-powered document queries',
  },
  {
    id: 'settings',
    path: '/configurations',
    icon: Settings,
    label: 'Settings',
    shortLabel: 'Config',
    description: 'System configuration',
  },
  {
    id: 'auth-management',
    path: '/configurations?tab=auth',
    matchPath: '/configurations',
    icon: Key,
    label: 'Auth Management',
    shortLabel: 'Auth',
    description: 'Manage API keys and OAuth2',
  },
  {
    id: 'reports',
    path: '/reports',
    icon: LayoutDashboard,
    label: 'Reports',
    shortLabel: 'Reports',
    description: 'Charts from saved queries',
  },
  {
    id: 'cad-3d',
    path: '/cad3d',
    icon: Package,
    label: 'CAD 3D',
    shortLabel: 'CAD',
    description: 'Convert STP to GLB and view',
  },
];

export const HEADER_QUICK_NAV_MAX = 8;

export function getPriorityRank(path) {
  const index = PRIORITY_PATHS.indexOf(String(path || '').split('?')[0]);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

// Finds the single most-specific nav item/child path matching a pathname —
// e.g. for "/order-x/email-inbox" both "/order-x" (OrderX V1) and
// "/order-x/email-inbox" itself are "/"-delimited prefixes, but only the
// longer, exact one should ever be treated as the actual match: "/order-x"
// is a distinct sibling page here, not an ancestor route of email-inbox, so
// checking each nav item in isolation (as isNavItemActive used to) let both
// match at once and left OrderX V1 highlighted everywhere under /order-x/*.
function getBestMatchingNavPath(pathname) {
  const normalized = String(pathname || '').split('?')[0];
  let bestPath = null;
  let bestLen = -1;
  const consider = (item) => {
    const base = String(item.path || '').split('?')[0];
    if (!base) return;
    if (base === '/dashboard') {
      if (normalized === '/' || normalized === '/dashboard') {
        if (base.length >= bestLen) { bestPath = base; bestLen = base.length; }
      }
      return;
    }
    if (normalized === base || normalized.startsWith(`${base}/`)) {
      if (base.length > bestLen) { bestPath = base; bestLen = base.length; }
    }
  };
  NAVIGATION_ITEMS.forEach((item) => {
    consider(item);
    item.children?.forEach((child) => consider(child));
  });
  return bestPath;
}

export function isNavItemActive(path, location) {
  const normalizedPath = String(path || '').split('?')[0];
  if (path === '/dashboard') {
    return location.pathname === '/' || location.pathname === '/dashboard';
  }
  if (path === '/products') {
    return location.pathname === '/products';
  }
  return getBestMatchingNavPath(location.pathname) === normalizedPath;
}

export function findNavItemForPathname(pathname, items = NAVIGATION_ITEMS) {
  const normalized = String(pathname || '').split('?')[0];
  let best = null;
  let bestLen = -1;

  const check = (item, returnItem) => {
    const base = String(item.matchPath || item.path || '').split('?')[0];
    if (!base || base === '/dashboard') {
      if (normalized === '/' || normalized === '/dashboard') {
        if (base.length >= bestLen) { best = returnItem; bestLen = base.length; }
      }
      return;
    }
    if (normalized === base || normalized.startsWith(`${base}/`)) {
      if (base.length > bestLen) { best = returnItem; bestLen = base.length; }
    }
  };

  items.forEach((item) => {
    check(item, item);
    // For grouped items, match children but credit the parent for usage tracking
    item.children?.forEach((child) => check(child, item));
  });

  return best;
}
